import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, X, Send, Loader2, MessageSquare, Mail, ChevronRight, CheckCircle2 } from "lucide-react";

const CATEGORIES = [
  { id: "bug", es: "🐛 Reportar un bug", en: "🐛 Report a bug" },
  { id: "billing", es: "💳 Cobros / Pagos", en: "💳 Billing / Payments" },
  { id: "feature", es: "💡 Sugerencia / Idea", en: "💡 Feature / Idea" },
  { id: "general", es: "❓ Pregunta general", en: "❓ General question" },
  { id: "other", es: "📨 Otro", en: "📨 Other" },
];

const SUPPORT_EMAIL = "soporte@bscan.app";

function NewTicketForm({ language, api, onCreated }) {
  const es = language === "es";
  const [category, setCategory] = useState("bug");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) {
      toast.error(es ? "Asunto demasiado corto" : "Subject too short");
      return;
    }
    if (description.trim().length < 10) {
      toast.error(es ? "Descripción demasiado corta" : "Description too short");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support/ticket", {
        subject: subject.trim(),
        description: description.trim(),
        category,
        page_url: typeof window !== "undefined" ? window.location.href : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      });
      toast.success(es ? "Ticket enviado — te responderemos pronto" : "Ticket sent — we'll reply soon");
      setSubject("");
      setDescription("");
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? "Error al enviar" : "Failed to send"));
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-white/50 block mb-1.5">
          {es ? "Categoría" : "Category"}
        </label>
        <div className="grid grid-cols-1 gap-1.5">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategory(c.id)}
              className={`text-left px-3 py-2 rounded-lg text-sm transition border ${
                category === c.id
                  ? "border-[#006CFF] bg-[#006CFF]/15 text-white"
                  : "border-white/10 bg-white/[0.02] text-white/70 hover:bg-white/[0.05]"
              }`}
              data-testid={`support-cat-${c.id}`}
            >
              {es ? c.es : c.en}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-white/50 block mb-1.5">
          {es ? "Asunto" : "Subject"}
        </label>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={es ? "Ej. No puedo entrar desde mi iPhone" : "e.g. Can't sign in from my iPhone"}
          className="bg-[#0A0A0A] border-white/15 text-white"
          required
          data-testid="support-subject"
        />
      </div>
      <div>
        <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-white/50 block mb-1.5">
          {es ? "Descripción" : "Description"}
        </label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={es
            ? "Cuéntanos qué pasó, qué intentabas hacer y qué error viste. Cuanto más detalle, más rápido te ayudamos."
            : "Tell us what happened, what you were trying to do, and what error you saw. More detail = faster help."}
          className="bg-[#0A0A0A] border-white/15 text-white min-h-[120px]"
          required
          data-testid="support-description"
        />
      </div>
      <Button type="submit" disabled={submitting} className="w-full bg-[#006CFF] hover:bg-[#0052CC] text-white" data-testid="support-submit">
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
        {es ? "Enviar ticket" : "Send ticket"}
      </Button>
      <div className="text-[10px] text-white/40 text-center">
        {es
          ? "Recibirás respuesta por email. También puedes verla aquí abajo."
          : "You'll get a reply by email. You can also see it here."}
      </div>
    </form>
  );
}

function TicketsList({ tickets, language, api, onRefresh }) {
  const es = language === "es";
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const STATUS_COLORS = {
    open: "#FFB800",
    in_progress: "#006CFF",
    waiting_user: "#7C3AED",
    resolved: "#10B981",
    closed: "#666",
  };
  const statusLabel = (s) => ({
    open: es ? "Abierto" : "Open",
    in_progress: es ? "En curso" : "In progress",
    waiting_user: es ? "Esperando tu respuesta" : "Awaiting your reply",
    resolved: es ? "Resuelto" : "Resolved",
    closed: es ? "Cerrado" : "Closed",
  }[s] || s);

  const sendReply = async (ticketId) => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/ticket/${ticketId}/reply`, { body: reply.trim() });
      setReply("");
      toast.success(es ? "Respuesta enviada" : "Reply sent");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSending(false); }
  };

  if (tickets.length === 0) {
    return <div className="text-xs text-white/40 italic text-center py-4">{es ? "Aún no has abierto tickets" : "No tickets yet"}</div>;
  }
  return (
    <div className="space-y-2">
      {tickets.map((t) => (
        <div key={t.id} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <button
            onClick={() => setOpenId(openId === t.id ? null : t.id)}
            className="w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-white/[0.04] transition"
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate font-medium">{t.subject}</div>
              <div className="text-[10px] font-mono text-white/40 mt-0.5">
                {new Date(t.created_at).toLocaleDateString()} · {t.messages?.length || 1} msg
              </div>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-[0.1em] px-2 py-0.5 rounded-full shrink-0"
                  style={{ backgroundColor: `${STATUS_COLORS[t.status]}20`, color: STATUS_COLORS[t.status] }}>
              {statusLabel(t.status)}
            </span>
            <ChevronRight className={`w-4 h-4 text-white/40 transition ${openId === t.id ? "rotate-90" : ""}`} />
          </button>
          {openId === t.id && (
            <div className="px-3 pb-3 space-y-2 border-t border-white/5 bg-black/20">
              <div className="space-y-2 mt-2 max-h-60 overflow-y-auto">
                {(t.messages || []).map((m, i) => (
                  <div key={i} className={`rounded-lg p-2 ${m.role === "admin" ? "bg-[#006CFF]/10 border border-[#006CFF]/30" : "bg-white/[0.03]"}`}>
                    <div className="text-[10px] font-mono uppercase text-white/40 mb-1">
                      {m.role === "admin" ? "BScan IA" : (es ? "Tú" : "You")} · {new Date(m.at).toLocaleString()}
                    </div>
                    <div className="text-xs text-white/85 whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
              </div>
              {t.status !== "closed" && t.status !== "resolved" && (
                <div className="flex gap-2 pt-2 border-t border-white/5">
                  <Input
                    value={openId === t.id ? reply : ""}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder={es ? "Tu respuesta..." : "Your reply..."}
                    className="bg-[#0A0A0A] border-white/15 text-white text-sm"
                  />
                  <Button size="sm" onClick={() => sendReply(t.id)} disabled={sending} className="bg-[#006CFF] hover:bg-[#0052CC] text-white">
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function SupportWidget() {
  const { language, api, user } = useApp();
  const location = useLocation();
  const es = language === "es";
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("new"); // new | history
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTickets = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await api.get("/support/my-tickets");
      setTickets(r.data.tickets || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { if (open && tab === "history") loadTickets(); }, [open, tab]);

  // Hide on landing/anonymous/pre-signup routes — those have their own conversion flow
  // and a floating support FAB distracts from the registration intent.
  const PRE_AUTH_ROUTES = ["/", "/try", "/probar", "/login", "/register", "/quick-start", "/auth/magic", "/install", "/android", "/iphone", "/ios", "/about", "/brand", "/acerca"];
  const isBlogOrLegal = location.pathname.startsWith("/blog") || location.pathname.startsWith("/terms") || location.pathname.startsWith("/privacy") || location.pathname.startsWith("/wellbeing");
  if (!user || PRE_AUTH_ROUTES.includes(location.pathname) || isBlogOrLegal) return null;

  return (
    <>
      <button
        type="button"
        aria-label={es ? "Soporte" : "Support"}
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#006CFF] hover:bg-[#0052CC] text-white shadow-lg shadow-[#006CFF]/30 flex items-center justify-center transition hover:scale-105"
        data-testid="support-fab"
      >
        <LifeBuoy className="w-6 h-6" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0C] border-white/15 text-white max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-[#006CFF]" /> {es ? "Soporte BScan IA" : "BScan IA Support"}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {es ? "¿En qué podemos ayudarte? Respuesta en menos de 24h." : "How can we help? Reply in under 24h."}
            </DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-black/30 rounded-lg">
            <button
              onClick={() => setTab("new")}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-mono uppercase tracking-[0.1em] transition ${tab === "new" ? "bg-[#006CFF] text-white" : "text-white/60 hover:text-white"}`}
              data-testid="support-tab-new"
            >
              {es ? "Nuevo ticket" : "New ticket"}
            </button>
            <button
              onClick={() => setTab("history")}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-mono uppercase tracking-[0.1em] transition ${tab === "history" ? "bg-[#006CFF] text-white" : "text-white/60 hover:text-white"}`}
              data-testid="support-tab-history"
            >
              {es ? "Mis tickets" : "My tickets"} {tickets.length > 0 && `(${tickets.length})`}
            </button>
          </div>

          {tab === "new" ? (
            <div className="space-y-4">
              <NewTicketForm language={language} api={api} onCreated={() => { setTab("history"); loadTickets(); }} />
              <div className="pt-3 border-t border-white/10">
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=BScan%20IA%20-%20`}
                  className="flex items-center justify-center gap-2 text-xs text-white/60 hover:text-white transition py-2"
                  data-testid="support-email-fallback"
                >
                  <Mail className="w-3.5 h-3.5" />
                  {es ? `O escríbenos a ${SUPPORT_EMAIL}` : `Or email ${SUPPORT_EMAIL}`}
                </a>
              </div>
            </div>
          ) : (
            <div>
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
              ) : (
                <TicketsList tickets={tickets} language={language} api={api} onRefresh={loadTickets} />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
